import React, { useState, useEffect, useRef } from 'react';
import './PromotionsView.css';

const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
function getToken() { return localStorage.getItem('token'); }

const PLAN_OPTIONS = [
  { value: 'free',       label: '🎁 Free Posting',    color: '#22c55e' },
  { value: 'discounted', label: '🏷️ Discounted',      color: '#f59e0b' },
  { value: 'premium',    label: '⭐ Premium Upgrade', color: '#6366f1' },
  { value: 'repost',     label: '🔄 Re-Post',         color: '#06b6d4' },
];

const PREMIUM_PLANS = [
  { key: 'slots_basic',    label: 'Basic',    color: '#64748b', icon: '🥉' },
  { key: 'slots_pro',      label: 'Pro',      color: '#6366f1', icon: '🥈' },
  { key: 'slots_business', label: 'Business', color: '#f59e0b', icon: '🥇' },
];

const STATUS_OPTIONS = ['All', 'Active', 'Expired', 'Inactive'];

const EMPTY_FORM = {
  emails: '', username_input: '', display_name: '', plan_type: 'free', discount_percent: '',
  notes: '', expires_at: '',
  slots_basic: '', slots_pro: '', slots_business: '',
};

function allSlotsZero(promo) {
  // All slot values — null/undefined/'' are treated as 0 (no unlimited mode).
  // A promotion is exhausted when every slot resolves to 0.
  // 'free' plan type is excluded — it has no slot tracking.
  if (promo.plan_type === 'free') return false;
  const toNum = v => (v === null || v === undefined || v === '') ? 0 : Number(v);
  return (
    toNum(promo.slots_basic)    === 0 &&
    toNum(promo.slots_pro)      === 0 &&
    toNum(promo.slots_business) === 0
  );
}

function statusBadgeClass(p) {
  if (!p.is_active) return 'badge-inactive';
  if (p.expires_at && new Date(p.expires_at) < new Date()) return 'badge-expired';
  if (allSlotsZero(p)) return 'badge-used';
  return 'badge-active';
}
function statusLabel(p) {
  if (!p.is_active) return 'Inactive';
  if (p.expires_at && new Date(p.expires_at) < new Date()) return 'Expired';
  if (allSlotsZero(p)) return 'Used';
  return 'Active';
}
function SlotCell({ value }) {
  const num  = (value === null || value === undefined || value === '') ? 0 : Number(value);
  const low  = num === 1;
  const zero = num === 0;
  return (
    <span
      className={`slot-value ${zero ? 'slot-zero' : low ? 'slot-low' : ''}`}
      title={`${num} slot${num !== 1 ? 's' : ''} remaining`}
    >
      {num}
    </span>
  );
}

function PromotionsView({ onLogout }) {
  const [promotions,          setPromotions]          = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [search,              setSearch]              = useState('');
  const [filterStatus,        setFilterStatus]        = useState('All');
  const [filterPlan,          setFilterPlan]          = useState('');
  const [showModal,           setShowModal]           = useState(false);
  const [editingId,           setEditingId]           = useState(null);
  const [form,                setForm]                = useState(EMPTY_FORM);
  const [saving,              setSaving]              = useState(false);
  const [error,               setError]               = useState('');
  // ✅ NEW: Active/All view toggle — default 'active' shows only truly active promos
  const [viewMode,            setViewMode]            = useState('active');
  const [resolvedUsername,    setResolvedUsername]    = useState('');
  const [resolvedRawUsername, setResolvedRawUsername] = useState('');
  const [lookingUp,           setLookingUp]           = useState(false);
  const lookupTimer = useRef(null);

  // ── Load ──────────────────────────────────────────────────────────────
  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
        headers: { Authorization: `Token ${getToken()}` },
      });
      if (res.status === 401 || res.status === 403) { onLogout?.(); return; }
      if (res.ok) {
        const d = await res.json();
        const list = d.results || d || [];
        setPromotions(list);
      } else {
        setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
      }
    } catch {
      setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPromotions(); }, []);

  // ── Live username lookup ──────────────────────────────────────────────
  useEffect(() => {
    const parts = form.emails.split(/[,\n]+/);
    const val   = parts[parts.length - 1].trim();
    setResolvedUsername('');
    setResolvedRawUsername('');
    if (!val || !val.includes('@')) {
      const emailCount = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length;
      if (emailCount <= 1) setForm(f => ({ ...f, username_input: '' }));
      return;
    }
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/admin/users/?search=${encodeURIComponent(val)}&page_size=5`,
          { headers: { Authorization: `Token ${getToken()}` } }
        );
        if (res.ok) {
          const data  = await res.json();
          const users = data.results || data || [];
          const match = users.find(u => (u.email || '').toLowerCase() === val.toLowerCase());
          if (match) {
            const fullName        = [match.first_name, match.last_name].filter(Boolean).join(' ').trim();
            const uname           = match.username || '';
            const isEmailUsername = uname.includes('@') || uname === val.split('@')[0];
            const display         = fullName || (!isEmailUsername ? uname : '') || uname.split('@')[0] || val.split('@')[0];
            setResolvedUsername(display);
            setResolvedRawUsername(match.username || '');
            setForm(f => ({ ...f, username_input: display }));
          } else {
            setResolvedUsername('⚠️ User not found in system');
            setResolvedRawUsername('');
            setForm(f => ({ ...f, username_input: '' }));
          }
        }
      } catch {}
      setLookingUp(false);
    }, 600);
    return () => clearTimeout(lookupTimer.current);
  }, [form.emails]);

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const emailList = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
    if (emailList.length === 0) { setError('At least one email is required.'); return; }
    if (form.plan_type === 'discounted' && !form.discount_percent) {
      setError('Enter a discount % for discounted plans.'); return;
    }
    setError('');
    setSaving(true);

    const basePayload = {
      ...form,
      username:         resolvedRawUsername || (form.username_input || ''),
      display_name:     (resolvedUsername && !resolvedUsername.startsWith('⚠️'))
                          ? resolvedUsername : (form.username_input || ''),
      discount_percent: form.discount_percent ? Number(form.discount_percent) : null,
      slots_basic:      form.slots_basic    !== '' ? Number(form.slots_basic)    : null,
      slots_pro:        form.slots_pro      !== '' ? Number(form.slots_pro)      : null,
      slots_business:   form.slots_business !== '' ? Number(form.slots_business) : null,
      is_active: true,
    };

    try {
      if (editingId) {
        const payload = { ...basePayload, identifier: emailList[0] };
        const res = await fetch(`${API_BASE_URL}/admin/promotions/${editingId}/`, {
          method: 'PUT',
          headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) { await fetchPromotions(); }
        else { saveLocally(payload); }
      } else {
        let anyFailed = false;
        for (const email of emailList) {
          const payload = { ...basePayload, identifier: email };
          try {
            const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
              method: 'POST',
              headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!res.ok) { anyFailed = true; saveLocally(payload); }
          } catch { anyFailed = true; saveLocally(payload); }
        }
        if (!anyFailed) { await fetchPromotions(); }
      }
    } catch {
      emailList.forEach(email => saveLocally({ ...basePayload, identifier: email }));
    }

    setSaving(false);
    closeModal();
  };

  const bulkDeleteExhausted = async () => {
    const exhausted = promotions.filter(allSlotsZero);
    if (exhausted.length === 0) return alert('No exhausted promotions to clean up!');
    if (!window.confirm(`Delete ${exhausted.length} exhausted promotion${exhausted.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    for (const p of exhausted) { await handleDelete(p.id, true); }
  };

  const saveLocally = (payload) => {
    const existing = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
    const updated  = editingId
      ? existing.map(p => p.id === editingId ? { ...p, ...payload } : p)
      : [{ ...payload, id: Date.now(), created_at: new Date().toISOString() }, ...existing];
    localStorage.setItem('admin_promotions', JSON.stringify(updated));
    setPromotions(updated);
  };

  const closeModal = () => {
    setShowModal(false); setEditingId(null);
    setForm(EMPTY_FORM); setResolvedUsername(''); setResolvedRawUsername(''); setError('');
  };

  const handleToggle = async (promo) => {
    const updated = { ...promo, is_active: !promo.is_active };
    try {
      await fetch(`${API_BASE_URL}/admin/promotions/${promo.id}/`, {
        method: 'PATCH',
        headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: updated.is_active }),
      });
    } catch {}
    const local = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
    localStorage.setItem('admin_promotions',
      JSON.stringify(local.map(p => p.id === promo.id ? { ...p, is_active: updated.is_active } : p))
    );
    setPromotions(prev => prev.map(p => p.id === promo.id ? updated : p));
  };

  const handleDelete = async (id, silent = false) => {
    if (!silent && !window.confirm('Remove this promotion?')) return;
    try {
      await fetch(`${API_BASE_URL}/admin/promotions/${id}/`, {
        method: 'DELETE', headers: { Authorization: `Token ${getToken()}` },
      });
    } catch {}
    const updated = JSON.parse(localStorage.getItem('admin_promotions') || '[]').filter(p => p.id !== id);
    localStorage.setItem('admin_promotions', JSON.stringify(updated));
    setPromotions(prev => prev.filter(p => p.id !== id));
  };

  const handleEdit = (promo) => {
    setForm({
      emails:           promo.identifier       || '',
      username_input:   promo.display_name || promo.username || '',
      plan_type:        promo.plan_type        || 'free',
      discount_percent: promo.discount_percent ?? '',
      notes:            promo.notes            || '',
      expires_at:       promo.expires_at ? promo.expires_at.slice(0, 10) : '',
      slots_basic:      promo.slots_basic      ?? '',
      slots_pro:        promo.slots_pro        ?? '',
      slots_business:   promo.slots_business   ?? '',
    });
    setResolvedUsername(promo.display_name || promo.username || '');
    setResolvedRawUsername(promo.username || '');
    setEditingId(promo.id);
    setError('');
    setShowModal(true);
  };

  const handleExport = () => {
    const rows = [
      ['Email/Username', 'Display Name', 'DB Username', 'Plan Type', 'Discount%',
       'Basic Slots', 'Pro Slots', 'Business Slots', 'Status', 'Expires', 'Notes'],
      ...promotions.map(p => [
        p.identifier, p.display_name || '', p.username || '', p.plan_type,
        p.discount_percent ?? '',
        p.slots_basic ?? '', p.slots_pro ?? '', p.slots_business ?? '',
        statusLabel(p), p.expires_at ? p.expires_at.slice(0, 10) : 'No expiry', p.notes || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const a   = document.createElement('a');
    a.href    = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `promotions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const filtered = promotions.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      p.identifier?.toLowerCase().includes(q) ||
      p.username?.toLowerCase().includes(q) ||
      p.notes?.toLowerCase().includes(q);
    // ✅ NEW: Active view = only truly active (not expired, not used, not inactive)
    const matchViewMode = viewMode === 'all' || statusLabel(p) === 'Active';
    return matchSearch &&
      matchViewMode &&
      (filterStatus === 'All' || statusLabel(p) === filterStatus) &&
      (!filterPlan || p.plan_type === filterPlan);
  });

  const counts = {
    total:   promotions.length,
    active:  promotions.filter(p => statusLabel(p) === 'Active').length,
    expired: promotions.filter(p => statusLabel(p) === 'Expired').length,
    free:    promotions.filter(p => p.plan_type === 'free').length,
  };

  const showSlots = !['free'].includes(form.plan_type);

  return (
    <div className="promotions-management">

      {/* ── Header ── */}
      <div className="promo-page-header">
        <div>
          <h2 className="promo-title">🎁 Promotions</h2>
          <p className="promo-subtitle">Manage discounted &amp; free posting deals for specific users</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* ✅ NEW: Active / All view toggle */}
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'active' ? 'view-toggle-btn-active' : ''}`}
              onClick={() => setViewMode('active')}
              title="Show only active promotions"
            >✅ Active</button>
            <button
              className={`view-toggle-btn ${viewMode === 'all' ? 'view-toggle-btn-active' : ''}`}
              onClick={() => setViewMode('all')}
              title="Show all promotions including expired and used"
            >📋 All</button>
          </div>

          <button className="btn-primary" onClick={() => {
            setForm(EMPTY_FORM); setEditingId(null);
            setResolvedUsername(''); setResolvedRawUsername('');
            setError(''); setShowModal(true);
          }}>
            + Add Promotion
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="promo-stats">
        {[
          { label: 'Total',   value: counts.total,   color: '#6366f1', icon: '🎁' },
          { label: 'Active',  value: counts.active,  color: '#22c55e', icon: '✅' },
          { label: 'Expired', value: counts.expired, color: '#ef4444', icon: '⏰' },
          { label: 'Free',    value: counts.free,    color: '#f59e0b', icon: '🆓' },
        ].map(s => (
          <div key={s.label} className="promo-stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
            <span className="promo-stat-icon">{s.icon}</span>
            <span className="promo-stat-value" style={{ color: s.color }}>{s.value}</span>
            <span className="promo-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="promo-filters">
        <input className="promo-search" placeholder="🔍 Search by email, username or notes…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="promo-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="promo-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
          <option value="">All Plans</option>
          {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <button className="btn-secondary" onClick={handleExport}>📊 Export CSV</button>
      </div>

      {/* ── Table ── */}
      <div className="promo-table-wrap">
        {loading ? (
          <div className="promo-loading">Loading promotions…</div>
        ) : filtered.length === 0 ? (
          <div className="promo-empty">
            <span>🎁</span>
            <p>No promotions yet. Click <strong>+ Add Promotion</strong> to get started.</p>
          </div>
        ) : (
          <table className="promo-table">
            <thead>
              <tr>
                <th>EMAIL</th>
                <th>USERNAME</th>
                <th>PLAN TYPE</th>
                <th>DISCOUNT</th>
                <th className="slot-header slot-basic">🥉 Basic<br/>Slots</th>
                <th className="slot-header slot-pro">🥈 Pro<br/>Slots</th>
                <th className="slot-header slot-biz">🥇 Business<br/>Slots</th>
                <th>STATUS</th>
                <th>EXPIRES</th>
                <th>NOTES</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(promo => {
                const plan        = PLAN_OPTIONS.find(p => p.value === promo.plan_type) || PLAN_OPTIONS[0];
                const isExhausted = allSlotsZero(promo);
                const rowClass    = isExhausted ? 'promo-row-exhausted' : '';
                return (
                  <tr key={promo.id} className={rowClass}>
                    <td className="promo-identifier">{promo.identifier}</td>
                    <td className="promo-username">
                      {(promo.display_name || promo.username) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span className="username-chip">{promo.display_name || promo.username}</span>
                          {promo.username && promo.display_name && promo.username !== promo.display_name && (
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>@{promo.username}</span>
                          )}
                        </div>
                      ) : <span className="slot-none">—</span>}
                    </td>
                    <td>
                      <span className="plan-badge" style={{ background: plan.color + '22', color: plan.color }}>
                        {plan.label}
                      </span>
                    </td>
                    <td className="promo-center">
                      {promo.plan_type === 'free' ? '100%' : promo.discount_percent ? `${promo.discount_percent}%` : '—'}
                    </td>
                    <td className="promo-center"><SlotCell value={promo.slots_basic} /></td>
                    <td className="promo-center"><SlotCell value={promo.slots_pro} /></td>
                    <td className="promo-center"><SlotCell value={promo.slots_business} /></td>
                    <td>
                      <span className={`status-badge ${statusBadgeClass(promo)}`}>{statusLabel(promo)}</span>
                    </td>
                    <td className="promo-center">
                      {promo.expires_at
                        ? promo.expires_at.slice(0, 10)
                        : <span style={{ color: '#94a3b8' }}>No expiry</span>}
                    </td>
                    <td className="promo-notes">{promo.notes || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    <td>
                      <div className="promo-actions">
                        <button className="btn-icon" title={promo.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggle(promo)}>
                          {promo.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button className="btn-icon" title="Edit" onClick={() => handleEdit(promo)}>✏️</button>
                        <button className="btn-icon btn-danger" title="Delete" onClick={() => handleDelete(promo.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div className="promo-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="promo-modal">
            <div className="promo-modal-header">
              <h3>{editingId ? 'Edit Promotion' : 'Add New Promotion'}</h3>
              <button className="promo-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="promo-modal-body">
              {error && <div className="promo-error">{error}</div>}

              {/* Email — supports multiple */}
              <label className="promo-label">
                Email *
                <span className="promo-optional" style={{ marginLeft: 6 }}>
                  — separate multiple emails with commas or new lines
                </span>
                <textarea
                  className="promo-input promo-email-textarea"
                  placeholder={"user1@example.com, user2@example.com\nor one per line"}
                  value={form.emails}
                  rows={3}
                  onChange={e => setForm(f => ({ ...f, emails: e.target.value }))}
                />
                {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length > 1 && (
                  <span className="email-count-badge">
                    📧 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} emails — will create {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} promotion records
                  </span>
                )}
                <div className="promo-lookup-result">
                  {lookingUp && <span className="lookup-searching">🔍 Looking up user…</span>}
                  {!lookingUp && resolvedUsername && !resolvedUsername.startsWith('⚠️') && (
                    <span className="lookup-found">
                      ✅ Found: <strong>{resolvedUsername}</strong>
                      {resolvedRawUsername && resolvedRawUsername !== resolvedUsername && (
                        <span style={{ color: '#86efac', fontWeight: 400, marginLeft: 6 }}>(@{resolvedRawUsername})</span>
                      )}
                    </span>
                  )}
                  {!lookingUp && resolvedUsername.startsWith?.('⚠️') && (
                    <span className="lookup-notfound">{resolvedUsername}</span>
                  )}
                </div>
              </label>

              {/* Username */}
              <label className="promo-label">
                Username <span className="promo-optional">(auto-filled from email lookup)</span>
                <input className="promo-input" placeholder="e.g. john_doe"
                  value={form.username_input}
                  onChange={e => setForm(f => ({ ...f, username_input: e.target.value }))} />
              </label>

              {/* Plan type */}
              <label className="promo-label">
                Plan Type *
                <select className="promo-input" value={form.plan_type}
                  onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}>
                  {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>

              {/* Discount % — only for discounted */}
              {form.plan_type === 'discounted' && (
                <label className="promo-label">
                  Discount Percentage *
                  <div className="promo-input-row">
                    <input className="promo-input" type="number" min="1" max="99" placeholder="e.g. 50"
                      value={form.discount_percent}
                      onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} />
                    <span className="promo-input-suffix">%</span>
                  </div>
                </label>
              )}

              {/* Slots — only for non-free plans */}
              {showSlots && (
                <div className="promo-slots-section">
                  <div className="promo-slots-title">
                    🎯 Premium Plan Promotion Slots
                    <span className="promo-optional"> — each post on that plan uses 1 slot</span>
                  </div>
                  <div className="promo-slots-grid">
                    {PREMIUM_PLANS.map(plan => (
                      <label key={plan.key} className="promo-slot-card" style={{ borderColor: plan.color + '55' }}>
                        <span className="promo-slot-icon">{plan.icon}</span>
                        <span className="promo-slot-name" style={{ color: plan.color }}>{plan.label}</span>
                        <input className="promo-input promo-slot-input" type="number" min="0" placeholder="0"
                          value={form[plan.key]}
                          onChange={e => setForm(f => ({ ...f, [plan.key]: e.target.value }))} />
                        <span className="promo-slot-unit">slots</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Expiry */}
              <label className="promo-label">
                Expiry Date <span className="promo-optional">(leave blank = no expiry)</span>
                <input className="promo-input" type="date"
                  value={form.expires_at} min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
              </label>

              {/* Notes */}
              <label className="promo-label">
                Notes <span className="promo-optional">(internal only)</span>
                <textarea className="promo-input promo-textarea"
                  placeholder="e.g. 3-month deal, referred by sales team"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </label>
            </div>

            <div className="promo-modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Add Promotion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsView;























// import React, { useState, useEffect, useRef } from 'react';
// import './PromotionsView.css';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
// function getToken() { return localStorage.getItem('token'); }

// const PLAN_OPTIONS = [
//   { value: 'free',       label: '🎁 Free Posting',    color: '#22c55e' },
//   { value: 'discounted', label: '🏷️ Discounted',      color: '#f59e0b' },
//   { value: 'premium',    label: '⭐ Premium Upgrade', color: '#6366f1' },
//   { value: 'repost',     label: '🔄 Re-Post',         color: '#06b6d4' },
// ];

// // 3 premium plans that support promotion slots (NOT free posting)
// const PREMIUM_PLANS = [
//   { key: 'slots_basic',    label: 'Basic',    color: '#64748b', icon: '🥉' },
//   { key: 'slots_pro',      label: 'Pro',      color: '#6366f1', icon: '🥈' },
//   { key: 'slots_business', label: 'Business', color: '#f59e0b', icon: '🥇' },
// ];

// const STATUS_OPTIONS = ['All', 'Active', 'Expired', 'Inactive'];

// const EMPTY_FORM = {
//   emails: '', username_input: '', display_name: '', plan_type: 'free', discount_percent: '',
//   notes: '', expires_at: '',
//   slots_basic: '', slots_pro: '', slots_business: '',
// };

// function statusBadgeClass(p) {
//   if (!p.is_active) return 'badge-inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'badge-expired';
//   return 'badge-active';
// }
// function statusLabel(p) {
//   if (!p.is_active) return 'Inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'Expired';
//   return 'Active';
// }
// function SlotCell({ value }) {
//   // Always show a number — never a dash. Null/undefined treated as 0.
//   const num = (value === null || value === undefined || value === '') ? 0 : Number(value);
//   const low  = num <= 1;
//   const zero = num === 0;
//   return (
//     <span
//       className={`slot-value ${zero ? 'slot-zero' : low ? 'slot-low' : ''}`}
//       title={`${num} slot${num !== 1 ? 's' : ''} remaining`}
//     >
//       {num}
//     </span>
//   );
// }

// // Auto-delete a promotion if all slot counts are 0
// function allSlotsZero(promo) {
//   const toNum = v => (v === null || v === undefined || v === '') ? 0 : Number(v);
//   return toNum(promo.slots_basic) === 0
//       && toNum(promo.slots_pro)   === 0
//       && toNum(promo.slots_business) === 0
//       && promo.plan_type !== 'free'
//       && promo.plan_type !== 'discounted';
// }

// // ─────────────────────────────────────────────────────────────────────────────
// function PromotionsView({ onLogout }) {
//   const [promotions,   setPromotions]   = useState([]);
//   const [loading,      setLoading]      = useState(true);
//   const [search,       setSearch]       = useState('');
//   const [filterStatus, setFilterStatus] = useState('All');
//   const [filterPlan,   setFilterPlan]   = useState('');
//   const [showModal,    setShowModal]    = useState(false);
//   const [editingId,    setEditingId]    = useState(null);
//   const [form,         setForm]         = useState(EMPTY_FORM);
//   const [saving,       setSaving]       = useState(false);
//   const [error,        setError]        = useState('');
//   const [resolvedUsername,    setResolvedUsername]    = useState('');
//   const [resolvedRawUsername, setResolvedRawUsername] = useState(''); // actual DB username
//   const [lookingUp,           setLookingUp]           = useState(false);
//   const lookupTimer = useRef(null);

//   // ── Load ──────────────────────────────────────────────────────────────
//   const fetchPromotions = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//         headers: { Authorization: `Token ${getToken()}` },
//       });
//       if (res.status === 401 || res.status === 403) { onLogout?.(); return; }
//       if (res.ok) { const d = await res.json(); setPromotions(d.results || d || []); }
//       else { setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]')); }
//     } catch {
//       setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
//     }
//     setLoading(false);
//   };
//   useEffect(() => { fetchPromotions(); }, []);

//   // ── Live username lookup when email is typed ──────────────────────────
//   useEffect(() => {
//     // For lookup use the LAST email typed (after last comma/newline)
//     const parts = form.emails.split(/[,\n]+/);
//     const val = parts[parts.length - 1].trim();
//     setResolvedUsername('');
//     setResolvedRawUsername('');
//     if (!val || !val.includes('@')) {
//       // Only clear username if single email mode (don't clear when bulk)
//       const emailCount = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length;
//       if (emailCount <= 1) setForm(f => ({ ...f, username_input: '' }));
//       return;
//     }
//     clearTimeout(lookupTimer.current);
//     lookupTimer.current = setTimeout(async () => {
//       setLookingUp(true);
//       try {
//         const res = await fetch(
//           `${API_BASE_URL}/admin/users/?search=${encodeURIComponent(val)}&page_size=5`,
//           { headers: { Authorization: `Token ${getToken()}` } }
//         );
//         if (res.ok) {
//           const data  = await res.json();
//           const users = data.results || data || [];
//           const match = users.find(u => (u.email || '').toLowerCase() === val.toLowerCase());
//           if (match) {
//             // Priority: full name (like UsersView shows) → username (if not email-shaped) → email prefix
//             const fullName = [match.first_name, match.last_name].filter(Boolean).join(' ').trim();
//             const uname    = match.username || '';
//             const isEmailUsername = uname.includes('@') || uname === val.split('@')[0];
//             const display  = fullName || (!isEmailUsername ? uname : '') || uname.split('@')[0] || val.split('@')[0];
//             setResolvedUsername(display);
//             setResolvedRawUsername(match.username || '');
//             // ✅ Auto-fill the username input field immediately
//             setForm(f => ({ ...f, username_input: display }));
//           } else {
//             setResolvedUsername('⚠️ User not found in system');
//             setResolvedRawUsername('');
//             setForm(f => ({ ...f, username_input: '' }));
//           }
//         }
//       } catch {}
//       setLookingUp(false);
//     }, 600);
//     return () => clearTimeout(lookupTimer.current);
//   }, [form.emails]);

//   // ── Save ──────────────────────────────────────────────────────────────
//   const handleSave = async () => {
//     const emailList = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
//     if (emailList.length === 0) { setError('At least one email is required.'); return; }
//     if (form.plan_type === 'discounted' && !form.discount_percent) {
//       setError('Enter a discount % for discounted plans.'); return;
//     }
//     setError('');
//     setSaving(true);
//     const basePayload = {
//       ...form,
//       username:         resolvedRawUsername || (form.username_input || ''),
//       display_name:     (resolvedUsername && !resolvedUsername.startsWith('⚠️')) ? resolvedUsername : (form.username_input || ''),
//       discount_percent: form.discount_percent ? Number(form.discount_percent) : null,
//       slots_basic:      form.slots_basic      !== '' ? Number(form.slots_basic)    : null,
//       slots_pro:        form.slots_pro        !== '' ? Number(form.slots_pro)      : null,
//       slots_business:   form.slots_business   !== '' ? Number(form.slots_business) : null,
//       is_active: true,
//     };

//     // ✅ Save one promotion record per email
//     try {
//       if (editingId) {
//         // Editing existing — single save as before
//         const payload = { ...basePayload, identifier: emailList[0] };
//         const res = await fetch(`${API_BASE_URL}/admin/promotions/${editingId}/`, {
//           method: 'PUT',
//           headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         });
//         if (res.ok) {
//           await fetchPromotions();
//           setPromotions(prev => { prev.forEach(p => autoDeleteIfEmpty(p)); return prev; });
//         } else { saveLocally(payload); }
//       } else {
//         // New — create one record per email (bulk)
//         let anyFailed = false;
//         for (const email of emailList) {
//           const payload = { ...basePayload, identifier: email };
//           try {
//             const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//               method: 'POST',
//               headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//               body: JSON.stringify(payload),
//             });
//             if (!res.ok) { anyFailed = true; saveLocally(payload); }
//           } catch { anyFailed = true; saveLocally(payload); }
//         }
//         if (!anyFailed) {
//           await fetchPromotions();
//           setPromotions(prev => { prev.forEach(p => autoDeleteIfEmpty(p)); return prev; });
//         }
//       }
//     } catch { emailList.forEach(email => saveLocally({ ...basePayload, identifier: email })); }
//     setSaving(false);
//     closeModal();
//   };

//   // ✅ Auto-delete promotion if all slot counts drop to 0
//   const autoDeleteIfEmpty = async (promo) => {
//     if (!allSlotsZero(promo)) return;
//     console.log(`🗑️ Auto-deleting promotion ${promo.id} — all slots are 0`);
//     await handleDelete(promo.id, true); // true = silent (no confirm dialog)
//   };

//   const saveLocally = (payload) => {
//     const existing = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     const updated  = editingId
//       ? existing.map(p => p.id === editingId ? { ...p, ...payload } : p)
//       : [{ ...payload, id: Date.now(), created_at: new Date().toISOString() }, ...existing];
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(updated);
//   };

//   const closeModal = () => {
//     setShowModal(false); setEditingId(null);
//     setForm(EMPTY_FORM); setResolvedUsername(''); setResolvedRawUsername(''); setError('');
//   };

//   const handleToggle = async (promo) => {
//     const updated = { ...promo, is_active: !promo.is_active };
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${promo.id}/`, {
//         method: 'PATCH',
//         headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ is_active: updated.is_active }),
//       });
//     } catch {}
//     const local = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     localStorage.setItem('admin_promotions',
//       JSON.stringify(local.map(p => p.id === promo.id ? { ...p, is_active: updated.is_active } : p))
//     );
//     setPromotions(prev => prev.map(p => p.id === promo.id ? updated : p));
//     // Auto-delete if all slots are now 0
//     autoDeleteIfEmpty(updated);
//   };

//   const handleDelete = async (id, silent = false) => {
//     if (!silent && !window.confirm('Remove this promotion?')) return;
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${id}/`, {
//         method: 'DELETE', headers: { Authorization: `Token ${getToken()}` },
//       });
//     } catch {}
//     const updated = JSON.parse(localStorage.getItem('admin_promotions') || '[]').filter(p => p.id !== id);
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(prev => prev.filter(p => p.id !== id));
//   };

//   const handleEdit = (promo) => {
//     setForm({
//       emails:           promo.identifier       || '',
//       plan_type:        promo.plan_type        || 'free',
//       discount_percent: promo.discount_percent ?? '',
//       notes:            promo.notes            || '',
//       expires_at:       promo.expires_at ? promo.expires_at.slice(0, 10) : '',
//       slots_basic:      promo.slots_basic      ?? '',
//       slots_pro:        promo.slots_pro        ?? '',
//       slots_business:   promo.slots_business   ?? '',
//     });
//     setResolvedUsername(promo.display_name || promo.username || '');
//     setResolvedRawUsername(promo.username || '');
//     setEditingId(promo.id);
//     setError('');
//     setShowModal(true);
//   };

//   const handleExport = () => {
//     const rows = [
//       ['Email/Username', 'Display Name', 'DB Username', 'Plan Type', 'Discount%', 'Max Ads',
//        'Basic Slots', 'Pro Slots', 'Business Slots', 'Status', 'Expires', 'Notes'],
//       ...promotions.map(p => [
//         p.identifier, p.display_name || '', p.username || '', p.plan_type, p.discount_percent ?? '',
//         p.slots_basic ?? '', p.slots_pro ?? '', p.slots_business ?? '',
//         statusLabel(p), p.expires_at ? p.expires_at.slice(0, 10) : 'No expiry', p.notes || '',
//       ]),
//     ];
//     const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
//     const a   = document.createElement('a');
//     a.href    = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
//     a.download = `promotions-${new Date().toISOString().slice(0, 10)}.csv`;
//     a.click();
//   };

//   const filtered = promotions.filter(p => {
//     const q = search.toLowerCase();
//     const matchSearch = !search ||
//       p.identifier?.toLowerCase().includes(q) ||
//       p.username?.toLowerCase().includes(q) ||
//       p.notes?.toLowerCase().includes(q);
//     return matchSearch &&
//       (filterStatus === 'All' || statusLabel(p) === filterStatus) &&
//       (!filterPlan || p.plan_type === filterPlan);
//   });

//   const counts = {
//     total:   promotions.length,
//     active:  promotions.filter(p => statusLabel(p) === 'Active').length,
//     expired: promotions.filter(p => statusLabel(p) === 'Expired').length,
//     free:    promotions.filter(p => p.plan_type === 'free').length,
//   };

//   const showSlots = !['free'].includes(form.plan_type);

//   return (
//     <div className="promotions-management">

//       {/* Header */}
//       <div className="promo-page-header">
//         <div>
//           <h2 className="promo-title">🎁 Promotions</h2>
//           <p className="promo-subtitle">Manage discounted & free posting deals for specific users</p>
//         </div>
//         <button className="btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setResolvedUsername(''); setError(''); setShowModal(true); }}>
//           + Add Promotion
//         </button>
//       </div>

//       {/* Stat cards */}
//       <div className="promo-stats">
//         {[
//           { label: 'Total',   value: counts.total,   color: '#6366f1', icon: '🎁' },
//           { label: 'Active',  value: counts.active,  color: '#22c55e', icon: '✅' },
//           { label: 'Expired', value: counts.expired, color: '#ef4444', icon: '⏰' },
//           { label: 'Free',    value: counts.free,    color: '#f59e0b', icon: '🆓' },
//         ].map(s => (
//           <div key={s.label} className="promo-stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
//             <span className="promo-stat-icon">{s.icon}</span>
//             <span className="promo-stat-value" style={{ color: s.color }}>{s.value}</span>
//             <span className="promo-stat-label">{s.label}</span>
//           </div>
//         ))}
//       </div>

//       {/* Filters */}
//       <div className="promo-filters">
//         <input className="promo-search" placeholder="🔍 Search by email, username or notes…"
//           value={search} onChange={e => setSearch(e.target.value)} />
//         <select className="promo-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
//           {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
//         </select>
//         <select className="promo-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
//           <option value="">All Plans</option>
//           {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//         </select>
//         <button className="btn-secondary" onClick={handleExport}>📊 Export CSV</button>
//       </div>

//       {/* Table */}
//       <div className="promo-table-wrap">
//         {loading ? (
//           <div className="promo-loading">Loading promotions…</div>
//         ) : filtered.length === 0 ? (
//           <div className="promo-empty">
//             <span>🎁</span>
//             <p>No promotions yet. Click <strong>+ Add Promotion</strong> to get started.</p>
//           </div>
//         ) : (
//           <table className="promo-table">
//             <thead>
//               <tr>
//                 <th>EMAIL</th>
//                 <th>USERNAME</th>
//                 <th>PLAN TYPE</th>
//                 <th>DISCOUNT</th>
//                 <th className="slot-header slot-basic">🥉 Basic<br/>Slots</th>
//                 <th className="slot-header slot-pro">🥈 Pro<br/>Slots</th>
//                 <th className="slot-header slot-biz">🥇 Business<br/>Slots</th>
//                 <th>STATUS</th>
//                 <th>EXPIRES</th>
//                 <th>NOTES</th>
//                 <th>ACTIONS</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filtered.map(promo => {
//                 const plan = PLAN_OPTIONS.find(p => p.value === promo.plan_type) || PLAN_OPTIONS[0];
//                 return (
//                   <tr key={promo.id}>
//                     <td className="promo-identifier">{promo.identifier}</td>
//                     <td className="promo-username">
//                       {(promo.display_name || promo.username)
//                         ? (
//                           <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                             <span className="username-chip">
//                               {promo.display_name || promo.username}
//                             </span>
//                             {promo.username && promo.display_name && promo.username !== promo.display_name && (
//                               <span style={{ fontSize: 10, color: '#94a3b8' }}>@{promo.username}</span>
//                             )}
//                           </div>
//                         )
//                         : <span className="slot-none">—</span>}
//                     </td>
//                     <td>
//                       <span className="plan-badge" style={{ background: plan.color + '22', color: plan.color }}>
//                         {plan.label}
//                       </span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.plan_type === 'free' ? '100%' : promo.discount_percent ? `${promo.discount_percent}%` : '—'}
//                     </td>
//                     <td className="promo-center"><SlotCell value={promo.slots_basic} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_pro} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_business} /></td>
//                     <td>
//                       <span className={`status-badge ${statusBadgeClass(promo)}`}>
//                         {statusLabel(promo)}
//                       </span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.expires_at
//                         ? promo.expires_at.slice(0, 10)
//                         : <span style={{ color: '#94a3b8' }}>No expiry</span>}
//                     </td>
//                     <td className="promo-notes">{promo.notes || <span style={{ color: '#94a3b8' }}>—</span>}</td>
//                     <td>
//                       <div className="promo-actions">
//                         <button className="btn-icon" title={promo.is_active ? 'Deactivate' : 'Activate'}
//                           onClick={() => handleToggle(promo)}>
//                           {promo.is_active ? '⏸️' : '▶️'}
//                         </button>
//                         <button className="btn-icon" title="Edit" onClick={() => handleEdit(promo)}>✏️</button>
//                         <button className="btn-icon btn-danger" title="Delete" onClick={() => handleDelete(promo.id)}>🗑️</button>
//                       </div>
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/* Modal */}
//       {showModal && (
//         <div className="promo-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
//           <div className="promo-modal">
//             <div className="promo-modal-header">
//               <h3>{editingId ? 'Edit Promotion' : 'Add New Promotion'}</h3>
//               <button className="promo-modal-close" onClick={closeModal}>✕</button>
//             </div>
//             <div className="promo-modal-body">
//               {error && <div className="promo-error">{error}</div>}

//               {/* Email — supports multiple emails */}
//               <label className="promo-label">
//                 Email *
//                 <span className="promo-optional" style={{ marginLeft: 6 }}>
//                   — separate multiple emails with commas or new lines
//                 </span>
//                 <textarea
//                   className="promo-input promo-email-textarea"
//                   placeholder={"user1@example.com, user2@example.com\nor one per line"}
//                   value={form.emails}
//                   onChange={e => setForm(f => ({ ...f, emails: e.target.value }))}
//                   rows={3}
//                 />
//                 {/* Email count badge */}
//                 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length > 1 && (
//                   <span className="email-count-badge">
//                     📧 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} emails — will create {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} promotion records
//                   </span>
//                 )}
//                 <div className="promo-lookup-result">
//                   {lookingUp && <span className="lookup-searching">🔍 Looking up user…</span>}
//                   {!lookingUp && resolvedUsername && !resolvedUsername.startsWith('⚠️') && (
//                     <span className="lookup-found">
//                       ✅ Found: <strong>{resolvedUsername}</strong>
//                       {resolvedRawUsername && resolvedRawUsername !== resolvedUsername && (
//                         <span style={{ color: '#86efac', fontWeight: 400, marginLeft: 6 }}>(@{resolvedRawUsername})</span>
//                       )}
//                     </span>
//                   )}
//                   {!lookingUp && resolvedUsername.startsWith('⚠️') && (
//                     <span className="lookup-notfound">{resolvedUsername}</span>
//                   )}
//                 </div>
//               </label>

//               {/* Optional manual username */}
//               <label className="promo-label">
//                 Username
//                 <input className="promo-input" placeholder="e.g. john_doe"
//                   value={form.username_input}
//                   onChange={e => setForm(f => ({ ...f, username_input: e.target.value }))} />
//               </label>

//               {/* Plan type */}
//               <label className="promo-label">
//                 Plan Type *
//                 <select className="promo-input" value={form.plan_type}
//                   onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}>
//                   {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//                 </select>
//               </label>

//               {/* Discount % */}
//               {form.plan_type === 'discounted' && (
//                 <label className="promo-label">
//                   Discount Percentage *
//                   <div className="promo-input-row">
//                     <input className="promo-input" type="number" min="1" max="99" placeholder="e.g. 50"
//                       value={form.discount_percent}
//                       onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} />
//                     <span className="promo-input-suffix">%</span>
//                   </div>
//                 </label>
//               )}


//               {/* Premium plan slots — only for non-free plans */}
//               {showSlots && (
//                 <div className="promo-slots-section">
//                   <div className="promo-slots-title">
//                     🎯 Premium Plan Promotion Slots
//                     <span className="promo-optional"> — each post on that plan uses 1 slot (counts down)</span>
//                   </div>
//                   <div className="promo-slots-grid">
//                     {PREMIUM_PLANS.map(plan => (
//                       <label key={plan.key} className="promo-slot-card" style={{ borderColor: plan.color + '55' }}>
//                         <span className="promo-slot-icon">{plan.icon}</span>
//                         <span className="promo-slot-name" style={{ color: plan.color }}>{plan.label}</span>
//                         <input className="promo-input promo-slot-input" type="number" min="0" placeholder="0"
//                           value={form[plan.key]}
//                           onChange={e => setForm(f => ({ ...f, [plan.key]: e.target.value }))} />
//                         <span className="promo-slot-unit">slots</span>
//                       </label>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Expiry */}
//               <label className="promo-label">
//                 Expiry Date <span className="promo-optional">(leave blank = no expiry)</span>
//                 <input className="promo-input" type="date"
//                   value={form.expires_at} min={new Date().toISOString().slice(0, 10)}
//                   onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
//               </label>

//               {/* Notes */}
//               <label className="promo-label">
//                 Notes <span className="promo-optional">(internal only)</span>
//                 <textarea className="promo-input promo-textarea"
//                   placeholder="e.g. 3-month deal, referred by sales team"
//                   value={form.notes}
//                   onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
//               </label>
//             </div>
//             <div className="promo-modal-footer">
//               <button className="btn-secondary" onClick={closeModal}>Cancel</button>
//               <button className="btn-primary" onClick={handleSave} disabled={saving}>
//                 {saving ? 'Saving…' : editingId ? 'Update' : 'Add Promotion'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default PromotionsView;




















// import React, { useState, useEffect, useRef } from 'react';
// import './PromotionsView.css';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
// function getToken() { return localStorage.getItem('token'); }

// const PLAN_OPTIONS = [
//   { value: 'free',       label: '🎁 Free Posting',    color: '#22c55e' },
//   { value: 'discounted', label: '🏷️ Discounted',      color: '#f59e0b' },
//   { value: 'premium',    label: '⭐ Premium Upgrade', color: '#6366f1' },
//   { value: 'repost',     label: '🔄 Re-Post',         color: '#06b6d4' },
// ];

// // 3 premium plans that support promotion slots (NOT free posting)
// const PREMIUM_PLANS = [
//   { key: 'slots_basic',    label: 'Basic',    color: '#64748b', icon: '🥉' },
//   { key: 'slots_pro',      label: 'Pro',      color: '#6366f1', icon: '🥈' },
//   { key: 'slots_business', label: 'Business', color: '#f59e0b', icon: '🥇' },
// ];

// const STATUS_OPTIONS = ['All', 'Active', 'Expired', 'Inactive'];

// const EMPTY_FORM = {
//   emails: '', username_input: '', display_name: '', plan_type: 'free', discount_percent: '',
//   notes: '', expires_at: '',
//   slots_basic: '', slots_pro: '', slots_business: '',
// };

// function statusBadgeClass(p) {
//   if (!p.is_active) return 'badge-inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'badge-expired';
//   return 'badge-active';
// }
// function statusLabel(p) {
//   if (!p.is_active) return 'Inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'Expired';
//   return 'Active';
// }
// function SlotCell({ value }) {
//   // Always show a number — never a dash. Null/undefined treated as 0.
//   const num = (value === null || value === undefined || value === '') ? 0 : Number(value);
//   const low  = num <= 1;
//   const zero = num === 0;
//   return (
//     <span
//       className={`slot-value ${zero ? 'slot-zero' : low ? 'slot-low' : ''}`}
//       title={`${num} slot${num !== 1 ? 's' : ''} remaining`}
//     >
//       {num}
//     </span>
//   );
// }

// // Auto-delete a promotion if all slot counts are 0
// function allSlotsZero(promo) {
//   const toNum = v => (v === null || v === undefined || v === '') ? 0 : Number(v);
//   return toNum(promo.slots_basic) === 0
//       && toNum(promo.slots_pro)   === 0
//       && toNum(promo.slots_business) === 0
//       && promo.plan_type !== 'free'
//       && promo.plan_type !== 'discounted';
// }

// // ─────────────────────────────────────────────────────────────────────────────
// function PromotionsView({ onLogout }) {
//   const [promotions,   setPromotions]   = useState([]);
//   const [loading,      setLoading]      = useState(true);
//   const [search,       setSearch]       = useState('');
//   const [filterStatus, setFilterStatus] = useState('All');
//   const [filterPlan,   setFilterPlan]   = useState('');
//   const [showModal,    setShowModal]    = useState(false);
//   const [editingId,    setEditingId]    = useState(null);
//   const [form,         setForm]         = useState(EMPTY_FORM);
//   const [saving,       setSaving]       = useState(false);
//   const [error,        setError]        = useState('');
//   const [resolvedUsername,    setResolvedUsername]    = useState('');
//   const [resolvedRawUsername, setResolvedRawUsername] = useState(''); // actual DB username
//   const [lookingUp,           setLookingUp]           = useState(false);
//   const lookupTimer = useRef(null);

//   // ── Load ──────────────────────────────────────────────────────────────
//   const fetchPromotions = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//         headers: { Authorization: `Token ${getToken()}` },
//       });
//       if (res.status === 401 || res.status === 403) { onLogout?.(); return; }
//       if (res.ok) { const d = await res.json(); setPromotions(d.results || d || []); }
//       else { setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]')); }
//     } catch {
//       setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
//     }
//     setLoading(false);
//   };
//   useEffect(() => { fetchPromotions(); }, []);

//   // ── Live username lookup when email is typed ──────────────────────────
//   useEffect(() => {
//     // For lookup use the LAST email typed (after last comma/newline)
//     const parts = form.emails.split(/[,\n]+/);
//     const val = parts[parts.length - 1].trim();
//     setResolvedUsername('');
//     setResolvedRawUsername('');
//     if (!val || !val.includes('@')) {
//       // Only clear username if single email mode (don't clear when bulk)
//       const emailCount = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length;
//       if (emailCount <= 1) setForm(f => ({ ...f, username_input: '' }));
//       return;
//     }
//     clearTimeout(lookupTimer.current);
//     lookupTimer.current = setTimeout(async () => {
//       setLookingUp(true);
//       try {
//         const res = await fetch(
//           `${API_BASE_URL}/admin/users/?search=${encodeURIComponent(val)}&page_size=5`,
//           { headers: { Authorization: `Token ${getToken()}` } }
//         );
//         if (res.ok) {
//           const data  = await res.json();
//           const users = data.results || data || [];
//           const match = users.find(u => (u.email || '').toLowerCase() === val.toLowerCase());
//           if (match) {
//             // Priority: full name (like UsersView shows) → username (if not email-shaped) → email prefix
//             const fullName = [match.first_name, match.last_name].filter(Boolean).join(' ').trim();
//             const uname    = match.username || '';
//             const isEmailUsername = uname.includes('@') || uname === val.split('@')[0];
//             const display  = fullName || (!isEmailUsername ? uname : '') || uname.split('@')[0] || val.split('@')[0];
//             setResolvedUsername(display);
//             setResolvedRawUsername(match.username || '');
//             // ✅ Auto-fill the username input field immediately
//             setForm(f => ({ ...f, username_input: display }));
//           } else {
//             setResolvedUsername('⚠️ User not found in system');
//             setResolvedRawUsername('');
//             setForm(f => ({ ...f, username_input: '' }));
//           }
//         }
//       } catch {}
//       setLookingUp(false);
//     }, 600);
//     return () => clearTimeout(lookupTimer.current);
//   }, [form.emails]);

//   // ── Save ──────────────────────────────────────────────────────────────
//   const handleSave = async () => {
//     const emailList = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
//     if (emailList.length === 0) { setError('At least one email is required.'); return; }
//     if (form.plan_type === 'discounted' && !form.discount_percent) {
//       setError('Enter a discount % for discounted plans.'); return;
//     }
//     setError('');
//     setSaving(true);
//     const basePayload = {
//       ...form,
//       username:         resolvedRawUsername || (form.username_input || ''),
//       display_name:     (resolvedUsername && !resolvedUsername.startsWith('⚠️')) ? resolvedUsername : (form.username_input || ''),
//       discount_percent: form.discount_percent ? Number(form.discount_percent) : null,
//       slots_basic:      form.slots_basic      !== '' ? Number(form.slots_basic)    : null,
//       slots_pro:        form.slots_pro        !== '' ? Number(form.slots_pro)      : null,
//       slots_business:   form.slots_business   !== '' ? Number(form.slots_business) : null,
//       is_active: true,
//     };

//     // ✅ Save one promotion record per email
//     try {
//       if (editingId) {
//         // Editing existing — single save as before
//         const payload = { ...basePayload, identifier: emailList[0] };
//         const res = await fetch(`${API_BASE_URL}/admin/promotions/${editingId}/`, {
//           method: 'PUT',
//           headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         });
//         if (res.ok) {
//           await fetchPromotions();
//           setPromotions(prev => { prev.forEach(p => autoDeleteIfEmpty(p)); return prev; });
//         } else { saveLocally(payload); }
//       } else {
//         // New — create one record per email (bulk)
//         let anyFailed = false;
//         for (const email of emailList) {
//           const payload = { ...basePayload, identifier: email };
//           try {
//             const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//               method: 'POST',
//               headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//               body: JSON.stringify(payload),
//             });
//             if (!res.ok) { anyFailed = true; saveLocally(payload); }
//           } catch { anyFailed = true; saveLocally(payload); }
//         }
//         if (!anyFailed) {
//           await fetchPromotions();
//           setPromotions(prev => { prev.forEach(p => autoDeleteIfEmpty(p)); return prev; });
//         }
//       }
//     } catch { emailList.forEach(email => saveLocally({ ...basePayload, identifier: email })); }
//     setSaving(false);
//     closeModal();
//   };

//   // ✅ Auto-delete promotion if all slot counts drop to 0
//   const autoDeleteIfEmpty = async (promo) => {
//     if (!allSlotsZero(promo)) return;
//     console.log(`🗑️ Auto-deleting promotion ${promo.id} — all slots are 0`);
//     await handleDelete(promo.id, true); // true = silent (no confirm dialog)
//   };

//   const saveLocally = (payload) => {
//     const existing = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     const updated  = editingId
//       ? existing.map(p => p.id === editingId ? { ...p, ...payload } : p)
//       : [{ ...payload, id: Date.now(), created_at: new Date().toISOString() }, ...existing];
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(updated);
//   };

//   const closeModal = () => {
//     setShowModal(false); setEditingId(null);
//     setForm(EMPTY_FORM); setResolvedUsername(''); setResolvedRawUsername(''); setError('');
//   };

//   const handleToggle = async (promo) => {
//     const updated = { ...promo, is_active: !promo.is_active };
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${promo.id}/`, {
//         method: 'PATCH',
//         headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ is_active: updated.is_active }),
//       });
//     } catch {}
//     const local = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     localStorage.setItem('admin_promotions',
//       JSON.stringify(local.map(p => p.id === promo.id ? { ...p, is_active: updated.is_active } : p))
//     );
//     setPromotions(prev => prev.map(p => p.id === promo.id ? updated : p));
//     // Auto-delete if all slots are now 0
//     autoDeleteIfEmpty(updated);
//   };

//   const handleDelete = async (id, silent = false) => {
//     if (!silent && !window.confirm('Remove this promotion?')) return;
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${id}/`, {
//         method: 'DELETE', headers: { Authorization: `Token ${getToken()}` },
//       });
//     } catch {}
//     const updated = JSON.parse(localStorage.getItem('admin_promotions') || '[]').filter(p => p.id !== id);
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(prev => prev.filter(p => p.id !== id));
//   };

//   const handleEdit = (promo) => {
//     setForm({
//       emails:           promo.identifier       || '',
//       plan_type:        promo.plan_type        || 'free',
//       discount_percent: promo.discount_percent ?? '',
//       notes:            promo.notes            || '',
//       expires_at:       promo.expires_at ? promo.expires_at.slice(0, 10) : '',
//       slots_basic:      promo.slots_basic      ?? '',
//       slots_pro:        promo.slots_pro        ?? '',
//       slots_business:   promo.slots_business   ?? '',
//     });
//     setResolvedUsername(promo.display_name || promo.username || '');
//     setResolvedRawUsername(promo.username || '');
//     setEditingId(promo.id);
//     setError('');
//     setShowModal(true);
//   };

//   const handleExport = () => {
//     const rows = [
//       ['Email/Username', 'Display Name', 'DB Username', 'Plan Type', 'Discount%', 'Max Ads',
//        'Basic Slots', 'Pro Slots', 'Business Slots', 'Status', 'Expires', 'Notes'],
//       ...promotions.map(p => [
//         p.identifier, p.display_name || '', p.username || '', p.plan_type, p.discount_percent ?? '',
//         p.slots_basic ?? '', p.slots_pro ?? '', p.slots_business ?? '',
//         statusLabel(p), p.expires_at ? p.expires_at.slice(0, 10) : 'No expiry', p.notes || '',
//       ]),
//     ];
//     const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
//     const a   = document.createElement('a');
//     a.href    = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
//     a.download = `promotions-${new Date().toISOString().slice(0, 10)}.csv`;
//     a.click();
//   };

//   const filtered = promotions.filter(p => {
//     const q = search.toLowerCase();
//     const matchSearch = !search ||
//       p.identifier?.toLowerCase().includes(q) ||
//       p.username?.toLowerCase().includes(q) ||
//       p.notes?.toLowerCase().includes(q);
//     return matchSearch &&
//       (filterStatus === 'All' || statusLabel(p) === filterStatus) &&
//       (!filterPlan || p.plan_type === filterPlan);
//   });

//   const counts = {
//     total:   promotions.length,
//     active:  promotions.filter(p => statusLabel(p) === 'Active').length,
//     expired: promotions.filter(p => statusLabel(p) === 'Expired').length,
//     free:    promotions.filter(p => p.plan_type === 'free').length,
//   };

//   const showSlots = !['free'].includes(form.plan_type);

//   return (
//     <div className="promotions-management">

//       {/* Header */}
//       <div className="promo-page-header">
//         <div>
//           <h2 className="promo-title">🎁 Promotions</h2>
//           <p className="promo-subtitle">Manage discounted & free posting deals for specific users</p>
//         </div>
//         <button className="btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setResolvedUsername(''); setError(''); setShowModal(true); }}>
//           + Add Promotion
//         </button>
//       </div>

//       {/* Stat cards */}
//       <div className="promo-stats">
//         {[
//           { label: 'Total',   value: counts.total,   color: '#6366f1', icon: '🎁' },
//           { label: 'Active',  value: counts.active,  color: '#22c55e', icon: '✅' },
//           { label: 'Expired', value: counts.expired, color: '#ef4444', icon: '⏰' },
//           { label: 'Free',    value: counts.free,    color: '#f59e0b', icon: '🆓' },
//         ].map(s => (
//           <div key={s.label} className="promo-stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
//             <span className="promo-stat-icon">{s.icon}</span>
//             <span className="promo-stat-value" style={{ color: s.color }}>{s.value}</span>
//             <span className="promo-stat-label">{s.label}</span>
//           </div>
//         ))}
//       </div>

//       {/* Filters */}
//       <div className="promo-filters">
//         <input className="promo-search" placeholder="🔍 Search by email, username or notes…"
//           value={search} onChange={e => setSearch(e.target.value)} />
//         <select className="promo-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
//           {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
//         </select>
//         <select className="promo-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
//           <option value="">All Plans</option>
//           {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//         </select>
//         <button className="btn-secondary" onClick={handleExport}>📊 Export CSV</button>
//       </div>

//       {/* Table */}
//       <div className="promo-table-wrap">
//         {loading ? (
//           <div className="promo-loading">Loading promotions…</div>
//         ) : filtered.length === 0 ? (
//           <div className="promo-empty">
//             <span>🎁</span>
//             <p>No promotions yet. Click <strong>+ Add Promotion</strong> to get started.</p>
//           </div>
//         ) : (
//           <table className="promo-table">
//             <thead>
//               <tr>
//                 <th>EMAIL</th>
//                 <th>USERNAME</th>
//                 <th>PLAN TYPE</th>
//                 <th>DISCOUNT</th>
//                 <th className="slot-header slot-basic">🥉 Basic<br/>Slots</th>
//                 <th className="slot-header slot-pro">🥈 Pro<br/>Slots</th>
//                 <th className="slot-header slot-biz">🥇 Business<br/>Slots</th>
//                 <th>STATUS</th>
//                 <th>EXPIRES</th>
//                 <th>NOTES</th>
//                 <th>ACTIONS</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filtered.map(promo => {
//                 const plan = PLAN_OPTIONS.find(p => p.value === promo.plan_type) || PLAN_OPTIONS[0];
//                 return (
//                   <tr key={promo.id}>
//                     <td className="promo-identifier">{promo.identifier}</td>
//                     <td className="promo-username">
//                       {(promo.display_name || promo.username)
//                         ? (
//                           <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                             <span className="username-chip">
//                               {promo.display_name || promo.username}
//                             </span>
//                             {promo.username && promo.display_name && promo.username !== promo.display_name && (
//                               <span style={{ fontSize: 10, color: '#94a3b8' }}>@{promo.username}</span>
//                             )}
//                           </div>
//                         )
//                         : <span className="slot-none">—</span>}
//                     </td>
//                     <td>
//                       <span className="plan-badge" style={{ background: plan.color + '22', color: plan.color }}>
//                         {plan.label}
//                       </span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.plan_type === 'free' ? '100%' : promo.discount_percent ? `${promo.discount_percent}%` : '—'}
//                     </td>
//                     <td className="promo-center"><SlotCell value={promo.slots_basic} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_pro} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_business} /></td>
//                     <td>
//                       <span className={`status-badge ${statusBadgeClass(promo)}`}>
//                         {statusLabel(promo)}
//                       </span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.expires_at
//                         ? promo.expires_at.slice(0, 10)
//                         : <span style={{ color: '#94a3b8' }}>No expiry</span>}
//                     </td>
//                     <td className="promo-notes">{promo.notes || <span style={{ color: '#94a3b8' }}>—</span>}</td>
//                     <td>
//                       <div className="promo-actions">
//                         <button className="btn-icon" title={promo.is_active ? 'Deactivate' : 'Activate'}
//                           onClick={() => handleToggle(promo)}>
//                           {promo.is_active ? '⏸️' : '▶️'}
//                         </button>
//                         <button className="btn-icon" title="Edit" onClick={() => handleEdit(promo)}>✏️</button>
//                         <button className="btn-icon btn-danger" title="Delete" onClick={() => handleDelete(promo.id)}>🗑️</button>
//                       </div>
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/* Modal */}
//       {showModal && (
//         <div className="promo-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
//           <div className="promo-modal">
//             <div className="promo-modal-header">
//               <h3>{editingId ? 'Edit Promotion' : 'Add New Promotion'}</h3>
//               <button className="promo-modal-close" onClick={closeModal}>✕</button>
//             </div>
//             <div className="promo-modal-body">
//               {error && <div className="promo-error">{error}</div>}

//               {/* Email — supports multiple emails */}
//               <label className="promo-label">
//                 Email *
//                 <span className="promo-optional" style={{ marginLeft: 6 }}>
//                   — separate multiple emails with commas or new lines
//                 </span>
//                 <textarea
//                   className="promo-input promo-email-textarea"
//                   placeholder={"user1@example.com, user2@example.com\nor one per line"}
//                   value={form.emails}
//                   onChange={e => setForm(f => ({ ...f, emails: e.target.value }))}
//                   rows={3}
//                 />
//                 {/* Email count badge */}
//                 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length > 1 && (
//                   <span className="email-count-badge">
//                     📧 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} emails — will create {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} promotion records
//                   </span>
//                 )}
//                 <div className="promo-lookup-result">
//                   {lookingUp && <span className="lookup-searching">🔍 Looking up user…</span>}
//                   {!lookingUp && resolvedUsername && !resolvedUsername.startsWith('⚠️') && (
//                     <span className="lookup-found">
//                       ✅ Found: <strong>{resolvedUsername}</strong>
//                       {resolvedRawUsername && resolvedRawUsername !== resolvedUsername && (
//                         <span style={{ color: '#86efac', fontWeight: 400, marginLeft: 6 }}>(@{resolvedRawUsername})</span>
//                       )}
//                     </span>
//                   )}
//                   {!lookingUp && resolvedUsername.startsWith('⚠️') && (
//                     <span className="lookup-notfound">{resolvedUsername}</span>
//                   )}
//                 </div>
//               </label>

//               {/* Optional manual username */}
//               <label className="promo-label">
//                 Username
//                 <input className="promo-input" placeholder="e.g. john_doe"
//                   value={form.username_input}
//                   onChange={e => setForm(f => ({ ...f, username_input: e.target.value }))} />
//               </label>

//               {/* Plan type */}
//               <label className="promo-label">
//                 Plan Type *
//                 <select className="promo-input" value={form.plan_type}
//                   onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}>
//                   {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//                 </select>
//               </label>

//               {/* Discount % */}
//               {form.plan_type === 'discounted' && (
//                 <label className="promo-label">
//                   Discount Percentage *
//                   <div className="promo-input-row">
//                     <input className="promo-input" type="number" min="1" max="99" placeholder="e.g. 50"
//                       value={form.discount_percent}
//                       onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} />
//                     <span className="promo-input-suffix">%</span>
//                   </div>
//                 </label>
//               )}


//               {/* Premium plan slots — only for non-free plans */}
//               {showSlots && (
//                 <div className="promo-slots-section">
//                   <div className="promo-slots-title">
//                     🎯 Premium Plan Promotion Slots
//                     <span className="promo-optional"> — each post on that plan uses 1 slot (counts down)</span>
//                   </div>
//                   <div className="promo-slots-grid">
//                     {PREMIUM_PLANS.map(plan => (
//                       <label key={plan.key} className="promo-slot-card" style={{ borderColor: plan.color + '55' }}>
//                         <span className="promo-slot-icon">{plan.icon}</span>
//                         <span className="promo-slot-name" style={{ color: plan.color }}>{plan.label}</span>
//                         <input className="promo-input promo-slot-input" type="number" min="0" placeholder="0"
//                           value={form[plan.key]}
//                           onChange={e => setForm(f => ({ ...f, [plan.key]: e.target.value }))} />
//                         <span className="promo-slot-unit">slots</span>
//                       </label>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Expiry */}
//               <label className="promo-label">
//                 Expiry Date <span className="promo-optional">(leave blank = no expiry)</span>
//                 <input className="promo-input" type="date"
//                   value={form.expires_at} min={new Date().toISOString().slice(0, 10)}
//                   onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
//               </label>

//               {/* Notes */}
//               <label className="promo-label">
//                 Notes <span className="promo-optional">(internal only)</span>
//                 <textarea className="promo-input promo-textarea"
//                   placeholder="e.g. 3-month deal, referred by sales team"
//                   value={form.notes}
//                   onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
//               </label>
//             </div>
//             <div className="promo-modal-footer">
//               <button className="btn-secondary" onClick={closeModal}>Cancel</button>
//               <button className="btn-primary" onClick={handleSave} disabled={saving}>
//                 {saving ? 'Saving…' : editingId ? 'Update' : 'Add Promotion'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default PromotionsView;































// import React, { useState, useEffect, useRef } from 'react';
// import './PromotionsView.css';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';
// function getToken() { return localStorage.getItem('token'); }

// const PLAN_OPTIONS = [
//   { value: 'free',       label: '🎁 Free Posting',    color: '#22c55e' },
//   { value: 'discounted', label: '🏷️ Discounted',      color: '#f59e0b' },
//   { value: 'premium',    label: '⭐ Premium Upgrade', color: '#6366f1' },
//   { value: 'repost',     label: '🔄 Re-Post',         color: '#06b6d4' },
// ];

// const PREMIUM_PLANS = [
//   { key: 'slots_basic',    label: 'Basic',    color: '#64748b', icon: '🥉' },
//   { key: 'slots_pro',      label: 'Pro',      color: '#6366f1', icon: '🥈' },
//   { key: 'slots_business', label: 'Business', color: '#f59e0b', icon: '🥇' },
// ];

// const STATUS_OPTIONS = ['All', 'Active', 'Expired', 'Inactive'];

// const EMPTY_FORM = {
//   emails: '', username_input: '', display_name: '', plan_type: 'free', discount_percent: '',
//   notes: '', expires_at: '',
//   slots_basic: '', slots_pro: '', slots_business: '',
// };

// function allSlotsZero(promo) {
//   // A promotion is "exhausted" when all three slot fields are explicitly 0.
//   // null/undefined/'' means admin left it blank = unlimited = NOT exhausted.
//   // ✅ FIX: 'discounted' was previously excluded so a discounted promo with
//   //    all slots at 0 would never be flagged — user kept the discount forever.
//   //    Now only 'free' is excluded (free promos have no slot tracking at all).
//   const isZero = v => v !== null && v !== undefined && v !== '' && Number(v) === 0;
//   const hasAnyConfigured = (
//     (promo.slots_basic    !== null && promo.slots_basic    !== undefined && promo.slots_basic    !== '') ||
//     (promo.slots_pro      !== null && promo.slots_pro      !== undefined && promo.slots_pro      !== '') ||
//     (promo.slots_business !== null && promo.slots_business !== undefined && promo.slots_business !== '')
//   );
//   return hasAnyConfigured
//       && isZero(promo.slots_basic)
//       && isZero(promo.slots_pro)
//       && isZero(promo.slots_business)
//       && promo.plan_type !== 'free';
// }

// function statusBadgeClass(p) {
//   if (!p.is_active) return 'badge-inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'badge-expired';
//   if (allSlotsZero(p)) return 'badge-used';
//   return 'badge-active';
// }
// function statusLabel(p) {
//   if (!p.is_active) return 'Inactive';
//   if (p.expires_at && new Date(p.expires_at) < new Date()) return 'Expired';
//   if (allSlotsZero(p)) return 'Used';
//   return 'Active';
// }
// function SlotCell({ value }) {
//   const num  = (value === null || value === undefined || value === '') ? 0 : Number(value);
//   const low  = num === 1;
//   const zero = num === 0;
//   return (
//     <span
//       className={`slot-value ${zero ? 'slot-zero' : low ? 'slot-low' : ''}`}
//       title={`${num} slot${num !== 1 ? 's' : ''} remaining`}
//     >
//       {num}
//     </span>
//   );
// }

// function PromotionsView({ onLogout }) {
//   const [promotions,          setPromotions]          = useState([]);
//   const [loading,             setLoading]             = useState(true);
//   const [search,              setSearch]              = useState('');
//   const [filterStatus,        setFilterStatus]        = useState('All');
//   const [filterPlan,          setFilterPlan]          = useState('');
//   const [showModal,           setShowModal]           = useState(false);
//   const [editingId,           setEditingId]           = useState(null);
//   const [form,                setForm]                = useState(EMPTY_FORM);
//   const [saving,              setSaving]              = useState(false);
//   const [error,               setError]               = useState('');
//   // ✅ NEW: Active/All view toggle — default 'active' shows only truly active promos
//   const [viewMode,            setViewMode]            = useState('active');
//   const [resolvedUsername,    setResolvedUsername]    = useState('');
//   const [resolvedRawUsername, setResolvedRawUsername] = useState('');
//   const [lookingUp,           setLookingUp]           = useState(false);
//   const lookupTimer = useRef(null);

//   // ── Load ──────────────────────────────────────────────────────────────
//   const fetchPromotions = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//         headers: { Authorization: `Token ${getToken()}` },
//       });
//       if (res.status === 401 || res.status === 403) { onLogout?.(); return; }
//       if (res.ok) {
//         const d = await res.json();
//         const list = d.results || d || [];
//         setPromotions(list);
//       } else {
//         setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
//       }
//     } catch {
//       setPromotions(JSON.parse(localStorage.getItem('admin_promotions') || '[]'));
//     }
//     setLoading(false);
//   };

//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   useEffect(() => { fetchPromotions(); }, []);

//   // ── Live username lookup ──────────────────────────────────────────────
//   useEffect(() => {
//     const parts = form.emails.split(/[,\n]+/);
//     const val   = parts[parts.length - 1].trim();
//     setResolvedUsername('');
//     setResolvedRawUsername('');
//     if (!val || !val.includes('@')) {
//       const emailCount = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length;
//       if (emailCount <= 1) setForm(f => ({ ...f, username_input: '' }));
//       return;
//     }
//     clearTimeout(lookupTimer.current);
//     lookupTimer.current = setTimeout(async () => {
//       setLookingUp(true);
//       try {
//         const res = await fetch(
//           `${API_BASE_URL}/admin/users/?search=${encodeURIComponent(val)}&page_size=5`,
//           { headers: { Authorization: `Token ${getToken()}` } }
//         );
//         if (res.ok) {
//           const data  = await res.json();
//           const users = data.results || data || [];
//           const match = users.find(u => (u.email || '').toLowerCase() === val.toLowerCase());
//           if (match) {
//             const fullName        = [match.first_name, match.last_name].filter(Boolean).join(' ').trim();
//             const uname           = match.username || '';
//             const isEmailUsername = uname.includes('@') || uname === val.split('@')[0];
//             const display         = fullName || (!isEmailUsername ? uname : '') || uname.split('@')[0] || val.split('@')[0];
//             setResolvedUsername(display);
//             setResolvedRawUsername(match.username || '');
//             setForm(f => ({ ...f, username_input: display }));
//           } else {
//             setResolvedUsername('⚠️ User not found in system');
//             setResolvedRawUsername('');
//             setForm(f => ({ ...f, username_input: '' }));
//           }
//         }
//       } catch {}
//       setLookingUp(false);
//     }, 600);
//     return () => clearTimeout(lookupTimer.current);
//   }, [form.emails]);

//   // ── Save ──────────────────────────────────────────────────────────────
//   const handleSave = async () => {
//     const emailList = form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
//     if (emailList.length === 0) { setError('At least one email is required.'); return; }
//     if (form.plan_type === 'discounted' && !form.discount_percent) {
//       setError('Enter a discount % for discounted plans.'); return;
//     }
//     setError('');
//     setSaving(true);

//     const basePayload = {
//       ...form,
//       username:         resolvedRawUsername || (form.username_input || ''),
//       display_name:     (resolvedUsername && !resolvedUsername.startsWith('⚠️'))
//                           ? resolvedUsername : (form.username_input || ''),
//       discount_percent: form.discount_percent ? Number(form.discount_percent) : null,
//       slots_basic:      form.slots_basic    !== '' ? Number(form.slots_basic)    : null,
//       slots_pro:        form.slots_pro      !== '' ? Number(form.slots_pro)      : null,
//       slots_business:   form.slots_business !== '' ? Number(form.slots_business) : null,
//       is_active: true,
//     };

//     try {
//       if (editingId) {
//         const payload = { ...basePayload, identifier: emailList[0] };
//         const res = await fetch(`${API_BASE_URL}/admin/promotions/${editingId}/`, {
//           method: 'PUT',
//           headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         });
//         if (res.ok) { await fetchPromotions(); }
//         else { saveLocally(payload); }
//       } else {
//         let anyFailed = false;
//         for (const email of emailList) {
//           const payload = { ...basePayload, identifier: email };
//           try {
//             const res = await fetch(`${API_BASE_URL}/admin/promotions/`, {
//               method: 'POST',
//               headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//               body: JSON.stringify(payload),
//             });
//             if (!res.ok) { anyFailed = true; saveLocally(payload); }
//           } catch { anyFailed = true; saveLocally(payload); }
//         }
//         if (!anyFailed) { await fetchPromotions(); }
//       }
//     } catch {
//       emailList.forEach(email => saveLocally({ ...basePayload, identifier: email }));
//     }

//     setSaving(false);
//     closeModal();
//   };

//   const bulkDeleteExhausted = async () => {
//     const exhausted = promotions.filter(allSlotsZero);
//     if (exhausted.length === 0) return alert('No exhausted promotions to clean up!');
//     if (!window.confirm(`Delete ${exhausted.length} exhausted promotion${exhausted.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
//     for (const p of exhausted) { await handleDelete(p.id, true); }
//   };

//   const saveLocally = (payload) => {
//     const existing = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     const updated  = editingId
//       ? existing.map(p => p.id === editingId ? { ...p, ...payload } : p)
//       : [{ ...payload, id: Date.now(), created_at: new Date().toISOString() }, ...existing];
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(updated);
//   };

//   const closeModal = () => {
//     setShowModal(false); setEditingId(null);
//     setForm(EMPTY_FORM); setResolvedUsername(''); setResolvedRawUsername(''); setError('');
//   };

//   const handleToggle = async (promo) => {
//     const updated = { ...promo, is_active: !promo.is_active };
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${promo.id}/`, {
//         method: 'PATCH',
//         headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ is_active: updated.is_active }),
//       });
//     } catch {}
//     const local = JSON.parse(localStorage.getItem('admin_promotions') || '[]');
//     localStorage.setItem('admin_promotions',
//       JSON.stringify(local.map(p => p.id === promo.id ? { ...p, is_active: updated.is_active } : p))
//     );
//     setPromotions(prev => prev.map(p => p.id === promo.id ? updated : p));
//   };

//   const handleDelete = async (id, silent = false) => {
//     if (!silent && !window.confirm('Remove this promotion?')) return;
//     try {
//       await fetch(`${API_BASE_URL}/admin/promotions/${id}/`, {
//         method: 'DELETE', headers: { Authorization: `Token ${getToken()}` },
//       });
//     } catch {}
//     const updated = JSON.parse(localStorage.getItem('admin_promotions') || '[]').filter(p => p.id !== id);
//     localStorage.setItem('admin_promotions', JSON.stringify(updated));
//     setPromotions(prev => prev.filter(p => p.id !== id));
//   };

//   const handleEdit = (promo) => {
//     setForm({
//       emails:           promo.identifier       || '',
//       username_input:   promo.display_name || promo.username || '',
//       plan_type:        promo.plan_type        || 'free',
//       discount_percent: promo.discount_percent ?? '',
//       notes:            promo.notes            || '',
//       expires_at:       promo.expires_at ? promo.expires_at.slice(0, 10) : '',
//       slots_basic:      promo.slots_basic      ?? '',
//       slots_pro:        promo.slots_pro        ?? '',
//       slots_business:   promo.slots_business   ?? '',
//     });
//     setResolvedUsername(promo.display_name || promo.username || '');
//     setResolvedRawUsername(promo.username || '');
//     setEditingId(promo.id);
//     setError('');
//     setShowModal(true);
//   };

//   const handleExport = () => {
//     const rows = [
//       ['Email/Username', 'Display Name', 'DB Username', 'Plan Type', 'Discount%',
//        'Basic Slots', 'Pro Slots', 'Business Slots', 'Status', 'Expires', 'Notes'],
//       ...promotions.map(p => [
//         p.identifier, p.display_name || '', p.username || '', p.plan_type,
//         p.discount_percent ?? '',
//         p.slots_basic ?? '', p.slots_pro ?? '', p.slots_business ?? '',
//         statusLabel(p), p.expires_at ? p.expires_at.slice(0, 10) : 'No expiry', p.notes || '',
//       ]),
//     ];
//     const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
//     const a   = document.createElement('a');
//     a.href    = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
//     a.download = `promotions-${new Date().toISOString().slice(0, 10)}.csv`;
//     a.click();
//   };

//   const filtered = promotions.filter(p => {
//     const q = search.toLowerCase();
//     const matchSearch = !search ||
//       p.identifier?.toLowerCase().includes(q) ||
//       p.username?.toLowerCase().includes(q) ||
//       p.notes?.toLowerCase().includes(q);
//     // ✅ NEW: Active view = only truly active (not expired, not used, not inactive)
//     const matchViewMode = viewMode === 'all' || statusLabel(p) === 'Active';
//     return matchSearch &&
//       matchViewMode &&
//       (filterStatus === 'All' || statusLabel(p) === filterStatus) &&
//       (!filterPlan || p.plan_type === filterPlan);
//   });

//   const counts = {
//     total:   promotions.length,
//     active:  promotions.filter(p => statusLabel(p) === 'Active').length,
//     expired: promotions.filter(p => statusLabel(p) === 'Expired').length,
//     free:    promotions.filter(p => p.plan_type === 'free').length,
//   };

//   const showSlots = !['free'].includes(form.plan_type);

//   return (
//     <div className="promotions-management">

//       {/* ── Header ── */}
//       <div className="promo-page-header">
//         <div>
//           <h2 className="promo-title">🎁 Promotions</h2>
//           <p className="promo-subtitle">Manage discounted &amp; free posting deals for specific users</p>
//         </div>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//           {/* ✅ NEW: Active / All view toggle */}
//           <div className="view-toggle">
//             <button
//               className={`view-toggle-btn ${viewMode === 'active' ? 'view-toggle-btn-active' : ''}`}
//               onClick={() => setViewMode('active')}
//               title="Show only active promotions"
//             >✅ Active</button>
//             <button
//               className={`view-toggle-btn ${viewMode === 'all' ? 'view-toggle-btn-active' : ''}`}
//               onClick={() => setViewMode('all')}
//               title="Show all promotions including expired and used"
//             >📋 All</button>
//           </div>

//           <button className="btn-primary" onClick={() => {
//             setForm(EMPTY_FORM); setEditingId(null);
//             setResolvedUsername(''); setResolvedRawUsername('');
//             setError(''); setShowModal(true);
//           }}>
//             + Add Promotion
//           </button>
//         </div>
//       </div>

//       {/* ── Stat cards ── */}
//       <div className="promo-stats">
//         {[
//           { label: 'Total',   value: counts.total,   color: '#6366f1', icon: '🎁' },
//           { label: 'Active',  value: counts.active,  color: '#22c55e', icon: '✅' },
//           { label: 'Expired', value: counts.expired, color: '#ef4444', icon: '⏰' },
//           { label: 'Free',    value: counts.free,    color: '#f59e0b', icon: '🆓' },
//         ].map(s => (
//           <div key={s.label} className="promo-stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
//             <span className="promo-stat-icon">{s.icon}</span>
//             <span className="promo-stat-value" style={{ color: s.color }}>{s.value}</span>
//             <span className="promo-stat-label">{s.label}</span>
//           </div>
//         ))}
//       </div>

//       {/* ── Filters ── */}
//       <div className="promo-filters">
//         <input className="promo-search" placeholder="🔍 Search by email, username or notes…"
//           value={search} onChange={e => setSearch(e.target.value)} />
//         <select className="promo-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
//           {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
//         </select>
//         <select className="promo-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
//           <option value="">All Plans</option>
//           {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//         </select>
//         <button className="btn-secondary" onClick={handleExport}>📊 Export CSV</button>
//       </div>

//       {/* ── Table ── */}
//       <div className="promo-table-wrap">
//         {loading ? (
//           <div className="promo-loading">Loading promotions…</div>
//         ) : filtered.length === 0 ? (
//           <div className="promo-empty">
//             <span>🎁</span>
//             <p>No promotions yet. Click <strong>+ Add Promotion</strong> to get started.</p>
//           </div>
//         ) : (
//           <table className="promo-table">
//             <thead>
//               <tr>
//                 <th>EMAIL</th>
//                 <th>USERNAME</th>
//                 <th>PLAN TYPE</th>
//                 <th>DISCOUNT</th>
//                 <th className="slot-header slot-basic">🥉 Basic<br/>Slots</th>
//                 <th className="slot-header slot-pro">🥈 Pro<br/>Slots</th>
//                 <th className="slot-header slot-biz">🥇 Business<br/>Slots</th>
//                 <th>STATUS</th>
//                 <th>EXPIRES</th>
//                 <th>NOTES</th>
//                 <th>ACTIONS</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filtered.map(promo => {
//                 const plan        = PLAN_OPTIONS.find(p => p.value === promo.plan_type) || PLAN_OPTIONS[0];
//                 const isExhausted = allSlotsZero(promo);
//                 const rowClass    = isExhausted ? 'promo-row-exhausted' : '';
//                 return (
//                   <tr key={promo.id} className={rowClass}>
//                     <td className="promo-identifier">{promo.identifier}</td>
//                     <td className="promo-username">
//                       {(promo.display_name || promo.username) ? (
//                         <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                           <span className="username-chip">{promo.display_name || promo.username}</span>
//                           {promo.username && promo.display_name && promo.username !== promo.display_name && (
//                             <span style={{ fontSize: 10, color: '#94a3b8' }}>@{promo.username}</span>
//                           )}
//                         </div>
//                       ) : <span className="slot-none">—</span>}
//                     </td>
//                     <td>
//                       <span className="plan-badge" style={{ background: plan.color + '22', color: plan.color }}>
//                         {plan.label}
//                       </span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.plan_type === 'free' ? '100%' : promo.discount_percent ? `${promo.discount_percent}%` : '—'}
//                     </td>
//                     <td className="promo-center"><SlotCell value={promo.slots_basic} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_pro} /></td>
//                     <td className="promo-center"><SlotCell value={promo.slots_business} /></td>
//                     <td>
//                       <span className={`status-badge ${statusBadgeClass(promo)}`}>{statusLabel(promo)}</span>
//                     </td>
//                     <td className="promo-center">
//                       {promo.expires_at
//                         ? promo.expires_at.slice(0, 10)
//                         : <span style={{ color: '#94a3b8' }}>No expiry</span>}
//                     </td>
//                     <td className="promo-notes">{promo.notes || <span style={{ color: '#94a3b8' }}>—</span>}</td>
//                     <td>
//                       <div className="promo-actions">
//                         <button className="btn-icon" title={promo.is_active ? 'Deactivate' : 'Activate'}
//                           onClick={() => handleToggle(promo)}>
//                           {promo.is_active ? '⏸️' : '▶️'}
//                         </button>
//                         <button className="btn-icon" title="Edit" onClick={() => handleEdit(promo)}>✏️</button>
//                         <button className="btn-icon btn-danger" title="Delete" onClick={() => handleDelete(promo.id)}>🗑️</button>
//                       </div>
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/* ── Modal ── */}
//       {showModal && (
//         <div className="promo-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
//           <div className="promo-modal">
//             <div className="promo-modal-header">
//               <h3>{editingId ? 'Edit Promotion' : 'Add New Promotion'}</h3>
//               <button className="promo-modal-close" onClick={closeModal}>✕</button>
//             </div>

//             <div className="promo-modal-body">
//               {error && <div className="promo-error">{error}</div>}

//               {/* Email — supports multiple */}
//               <label className="promo-label">
//                 Email *
//                 <span className="promo-optional" style={{ marginLeft: 6 }}>
//                   — separate multiple emails with commas or new lines
//                 </span>
//                 <textarea
//                   className="promo-input promo-email-textarea"
//                   placeholder={"user1@example.com, user2@example.com\nor one per line"}
//                   value={form.emails}
//                   rows={3}
//                   onChange={e => setForm(f => ({ ...f, emails: e.target.value }))}
//                 />
//                 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length > 1 && (
//                   <span className="email-count-badge">
//                     📧 {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} emails — will create {form.emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean).length} promotion records
//                   </span>
//                 )}
//                 <div className="promo-lookup-result">
//                   {lookingUp && <span className="lookup-searching">🔍 Looking up user…</span>}
//                   {!lookingUp && resolvedUsername && !resolvedUsername.startsWith('⚠️') && (
//                     <span className="lookup-found">
//                       ✅ Found: <strong>{resolvedUsername}</strong>
//                       {resolvedRawUsername && resolvedRawUsername !== resolvedUsername && (
//                         <span style={{ color: '#86efac', fontWeight: 400, marginLeft: 6 }}>(@{resolvedRawUsername})</span>
//                       )}
//                     </span>
//                   )}
//                   {!lookingUp && resolvedUsername.startsWith?.('⚠️') && (
//                     <span className="lookup-notfound">{resolvedUsername}</span>
//                   )}
//                 </div>
//               </label>

//               {/* Username */}
//               <label className="promo-label">
//                 Username <span className="promo-optional">(auto-filled from email lookup)</span>
//                 <input className="promo-input" placeholder="e.g. john_doe"
//                   value={form.username_input}
//                   onChange={e => setForm(f => ({ ...f, username_input: e.target.value }))} />
//               </label>

//               {/* Plan type */}
//               <label className="promo-label">
//                 Plan Type *
//                 <select className="promo-input" value={form.plan_type}
//                   onChange={e => setForm(f => ({ ...f, plan_type: e.target.value }))}>
//                   {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//                 </select>
//               </label>

//               {/* Discount % — only for discounted */}
//               {form.plan_type === 'discounted' && (
//                 <label className="promo-label">
//                   Discount Percentage *
//                   <div className="promo-input-row">
//                     <input className="promo-input" type="number" min="1" max="99" placeholder="e.g. 50"
//                       value={form.discount_percent}
//                       onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} />
//                     <span className="promo-input-suffix">%</span>
//                   </div>
//                 </label>
//               )}

//               {/* Slots — only for non-free plans */}
//               {showSlots && (
//                 <div className="promo-slots-section">
//                   <div className="promo-slots-title">
//                     🎯 Premium Plan Promotion Slots
//                     <span className="promo-optional"> — each post on that plan uses 1 slot</span>
//                   </div>
//                   <div className="promo-slots-grid">
//                     {PREMIUM_PLANS.map(plan => (
//                       <label key={plan.key} className="promo-slot-card" style={{ borderColor: plan.color + '55' }}>
//                         <span className="promo-slot-icon">{plan.icon}</span>
//                         <span className="promo-slot-name" style={{ color: plan.color }}>{plan.label}</span>
//                         <input className="promo-input promo-slot-input" type="number" min="0" placeholder="0"
//                           value={form[plan.key]}
//                           onChange={e => setForm(f => ({ ...f, [plan.key]: e.target.value }))} />
//                         <span className="promo-slot-unit">slots</span>
//                       </label>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Expiry */}
//               <label className="promo-label">
//                 Expiry Date <span className="promo-optional">(leave blank = no expiry)</span>
//                 <input className="promo-input" type="date"
//                   value={form.expires_at} min={new Date().toISOString().slice(0, 10)}
//                   onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
//               </label>

//               {/* Notes */}
//               <label className="promo-label">
//                 Notes <span className="promo-optional">(internal only)</span>
//                 <textarea className="promo-input promo-textarea"
//                   placeholder="e.g. 3-month deal, referred by sales team"
//                   value={form.notes}
//                   onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
//               </label>
//             </div>

//             <div className="promo-modal-footer">
//               <button className="btn-secondary" onClick={closeModal}>Cancel</button>
//               <button className="btn-primary" onClick={handleSave} disabled={saving}>
//                 {saving ? 'Saving…' : editingId ? 'Update' : 'Add Promotion'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default PromotionsView;
















